import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import useTranslation from '../hooks/useTranslation';
import './Navbar.css';

const Navbar = () => {
  const { t, lang, isRTL, toggleLang } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { key: 'nav.home', href: '#home' },
    { key: 'nav.services', href: '#services' },
    { key: 'nav.about', href: '#about' },
    { key: 'nav.contact', href: '#contact' },
  ];

  const handleNavClick = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header
      className={`navbar${scrolled ? ' navbar--scrolled' : ''}${menuOpen ? ' navbar--open' : ''}`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      role="banner"
    >
      <div className="navbar__inner container">
        {/* Logo */}
        <a
          href="#home"
          className="navbar__logo"
          aria-label="Orkina Media Production"
          onClick={(e) => { e.preventDefault(); handleNavClick('#home'); }}
        >
          <img
            src="/orkina-logo-white.png"
            alt="Orkina Media Production Logo"
            width="160"
            height="52"
            loading="eager"
          />
        </a>

        {/* Desktop nav links */}
        <nav className="navbar__links" aria-label={isRTL ? 'القائمة الرئيسية' : 'Main navigation'}>
          {navLinks.map(({ key, href }) => (
            <a
              key={key}
              href={href}
              className="navbar__link"
              onClick={(e) => { e.preventDefault(); handleNavClick(href); }}
            >
              {t(key)}
            </a>
          ))}
        </nav>

        {/* Right side: language toggle + hamburger */}
        <div className="navbar__actions">
          <button
            className="navbar__lang"
            onClick={toggleLang}
            aria-label={lang === 'ar' ? 'Switch to English' : 'التبديل إلى العربية'}
            id="lang-toggle-btn"
          >
            <span>{lang === 'ar' ? 'EN' : 'AR'}</span>
          </button>

          <button
            className={`navbar__burger${menuOpen ? ' active' : ''}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <motion.div
        className="navbar__mobile"
        initial={false}
        animate={menuOpen ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
      >
        <nav aria-label={isRTL ? 'القائمة المحمولة' : 'Mobile navigation'}>
          {navLinks.map(({ key, href }) => (
            <a
              key={key}
              href={href}
              className="navbar__mobile-link"
              onClick={(e) => { e.preventDefault(); handleNavClick(href); }}
            >
              {t(key)}
            </a>
          ))}
        </nav>
      </motion.div>
    </motion.header>
  );
};

export default Navbar;
